import * as React from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { Button, CircularProgress, Typography } from '@mui/material';
import { GetApp } from '@mui/icons-material';
import { formatDateTime } from '../ex_dayjs';

/** Sort spec forwarded to the `export` data-provider method. */
interface ExportSort {
    field: string;
    order: string;
}

/** Pagination spec forwarded to the `export` data-provider method. */
interface ExportPagination {
    page: number;
    perPage: number;
}

/**
 * Params forwarded to the `export` data-provider method, e.g.
 * `{ filter: filterValues, pagination: { page, perPage }, sort }`.
 */
interface RaExporterParams {
    filter?: Record<string, unknown>;
    pagination?: ExportPagination;
    sort?: ExportSort;
}

/**
 * Export file info, e.g.
 * `{ filename: 'SourceVariable-...', columns: [...], limit: 1000000, position: '3-2' }`.
 */
export interface RaExporterFile {
    /** Download filename. Defaults to `` `${resource}-${formatDateTime(new Date(), 'YY-MM-DD')}` `` when omitted. */
    filename?: string;
    /** Button label. Default: `'Export'`. */
    label?: string;
    /** Columns to include in the export. */
    columns?: string[];
    /** Max rows to export; forced up to at least 1,000,000 by `handleClick`. */
    limit?: number;
    /** Server-side export layout position. */
    position?: string;
}

/** Result returned by {@link RaExporterProps.onPrepare}. */
interface RaExporterPrepared {
    resource: string;
    params: RaExporterParams;
    exportFile: RaExporterFile;
}

/** Props for {@link RaExporter}. */
interface RaExporterProps {
    /** Resource name. Optional — e.g. unset on the AG Grid path, which falls back to `agSource`. */
    resource?: string;
    /** Params ex: `{ filter: filterValues, pagination: {page, perPage}, sort }`. */
    params: RaExporterParams;
    /** Export file info (see {@link RaExporterFile}). */
    exportFile: RaExporterFile;
    /** Function to prepare params before export; returns `{ resource, params, exportFile }`, or a falsy value to cancel the export. */
    onPrepare?: () => RaExporterPrepared | null | undefined;
}

/**
 * Export button that downloads a `.xlsx`/`.csv` file produced by the `export` data-provider
 * method, showing a determinate progress spinner while the response streams in.
 */
const RaExporter: React.FC<RaExporterProps> = ({ resource, params, exportFile, onPrepare }) => {
    const [disabled, setDisabled] = React.useState(false);
    const [isLoading, setLoading] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const handleClick = async () => {
        // Local copies: `resource`/`params`/`exportFile` are owned by the caller, so build fresh
        // objects here instead of assigning into/mutating the props (or the onPrepare() result).
        let _resource = resource, _params = params, _exportFile = exportFile;
        if (onPrepare) {
            let _p = onPrepare();
            if (!_p) return;
            _resource = _p.resource;
            _params = _p.params;
            _exportFile = _p.exportFile;
        }
        setLoading(true);
        setDisabled(true);
        setProgress(0);

        // Set default if null
        const sort = _params.sort || { field: 'id', order: 'asc' };
        const pagination = {
            page: _params.pagination?.page ?? 1,
            perPage: Math.max(_params.pagination?.perPage ?? (_exportFile.limit || 0), 1000000)
        };
        const finalParams: RaExporterParams = { ..._params, sort, pagination };
        const finalExportFile: RaExporterFile = {
            ..._exportFile,
            filename: _exportFile.filename || `${_resource}-${formatDateTime(new Date(), 'YY-MM-DD')}`
        };

        await dataProvider.export(_resource, finalParams, finalExportFile)
            .then(async (resp: Response) => {
                if (resp.ok) await downloadFile(resp, finalExportFile);
                else resetState();
            })
            .catch((error: unknown) => {
                resetState();
                notify(`Export has error ${error}`, { type: 'error' });
            });
    };

    function resetState () {
        setLoading(false);
        setDisabled(false);
        setProgress(100);
    }

    async function downloadFile (response: Response, exportFile: RaExporterFile) {
        let contentType = response.headers.get('Content-Type');
        if (contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || contentType === 'text/csv') {
            let totalSize = parseInt(response.headers.get('Content-Length') || '', 10); // Get the total size, NaN when absent
            let downloadedSize = 0, downloadedParts: Uint8Array[] = [];
            const reader = response.body!.getReader();
            while (true) {
                const { done, value } = await reader.read();
                let length = value?.length || 0;
                if (done) break;
                downloadedParts.push(value);
                // Calculate the percentage downloaded
                downloadedSize += length;
                // Skip the update when Content-Length is missing/invalid, rather than reporting Infinity/NaN%
                if (totalSize > 0) {
                    const percentComplete = (downloadedSize / totalSize) * 100;
                    setProgress(percentComplete);
                }
            }
            const mergedBlob = new Blob(downloadedParts as BlobPart[], { type: contentType });
            const mergedUrl = window.URL.createObjectURL(mergedBlob);
            const downloadLink = document.createElement('a');
            downloadLink.href = mergedUrl;
            downloadLink.download = exportFile.filename as string;
            downloadLink.click();
            console.log('Saved: ' + new Date());
            resetState();
        }
    }
    return (
        <Button size="small"
            startIcon={(isLoading && progress < 100) ?
                (<div style={{ position: 'relative', maxHeight: 20 }}>
                    <CircularProgress size={20} variant="determinate" color="inherit" value={progress} thickness={5} />
                    <Typography
                        fontSize={8}
                        color="textSecondary"
                        style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                    >
                        {`${Math.round(progress)}%`}
                    </Typography>
                </div>) : (<GetApp />)}
            onClick={handleClick}
            disabled={disabled}>
            {exportFile?.label || 'Export'}
        </Button>
    );
};

export default React.memo(RaExporter);
